import { useState } from 'react'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackSkillDeleted } from '@/analytics/utils/skill-analytics'
import { Modal, ModalContent, ModalFooterWithActions, ModalFooterWithActionsType } from '@/components/modal'
import type { UserSkill } from '@/user-skills/schemas'

type DeleteUserSkillDialogProps = {
  skill: UserSkill
  onClose: () => void
  onDelete: () => Promise<void>
}

export default function DeleteUserSkillDialog({ skill, onClose, onDelete }: DeleteUserSkillDialogProps) {
  const { trackEvent } = useAnalytics()
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(false)

  const handleDelete = async () => {
    if (deleting) return
    setDeleting(true)
    setDeleteError(false)
    try {
      await onDelete()
      trackSkillDeleted(trackEvent, skill.id)
      onClose()
    } catch {
      setDeleteError(true)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Modal open onOpenChange={(open) => !open && !deleting && onClose()}>
      <ModalContent
        title="Delete skill?"
        subtitle={`“${skill.name}” will be permanently deleted. This can’t be undone.`}
        width="sm"
        height="max-sm"
        preventDismiss={deleting}
        footer={
          <ModalFooterWithActions
            type={ModalFooterWithActionsType.DESTRUCTIVE}
            inlineMessage={deleteError ? { variant: 'error', children: 'Failed to delete your skill. Please try again.' } : undefined}
            secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: deleting }}
            primaryAction={{ label: 'Delete', onClick: handleDelete, loading: deleting, disabled: deleting }}
          />
        }
      />
    </Modal>
  )
}
